import { type Event, EventListener, KeyCodes, NaturalComparator } from "silver";

type SortClasses = Record<"SORTABLE"|"SORT_ASC"|"SORT_DESC"|"SORTED"|"HIDDEN_BY_FILTER", string>;

const Classes : SortClasses = {
	SORTABLE:         "-sortable",
	SORT_ASC:         "-sortAsc",
	SORT_DESC:        "-sortDesc",
	SORTED:           "-sorted",
	HIDDEN_BY_FILTER: "-hidden"
};

export type ColumnSortDirection = "asc"|"desc"|"auto";

interface ColLookup {
	[ row: string ]: {
		[ column: string ]: number
	};
}

interface ColumnSort {
	column: number;
	direction: ColumnSortDirection;
}

interface ColumnSortedEvent extends Event {
	sort: ColumnSort;
	sender: TableSorter;
}

export class TableSorter {

	public readonly onColumnSorted = EventListener.create<ColumnSortedEvent>("columnSorted");

	private columnIsDisplayedCache: { [ columnIndex: number ]: boolean } = { };
	private readonly thCols: ColLookup;
	private sortList: ColumnSort[] = [];
	private comparator = new NaturalComparator;
	private classes: SortClasses;

	constructor(
		private table: HTMLTableElement,
		classes: Partial<SortClasses> = { },
		private cacheCellDisplay: boolean = false
	) {
		this.classes = { ...Classes, ...classes };
		this.columnIsDisplayedCache = { };
		this.thCols = TableSorter.getTHColumnIndexes(this.table);
		this.init();
	}

	public static getTHColumnIndexes(t: HTMLTableElement): ColLookup {

		const matrix: Array<Array<undefined|true>> = [];
		const thead = t.tHead;
		if (!thead) {
			throw new Error("No table head");
		}
		const trs = Array.from(thead.getElementsByTagName("TR")) as HTMLTableRowElement[];
		let firstAvailCol: number = 0;

		const lookup: ColLookup = { };
		for (let i = 0; i < trs.length; i++) {
			const cells = trs[i].cells;
			for (let j = 0; j < cells.length; j++) {
				const c = cells[j];

				const rowIndex = trs[ i ].rowIndex;
				const rowSpan = c.rowSpan || 1;
				const colSpan = c.colSpan || 1;

				if (! matrix.hasOwnProperty(rowIndex)) {
					matrix[rowIndex] = [];
				}
				// Find first available column in the first row
				for (let k = 0; k < matrix[rowIndex].length + 1; k++) {
					if (! matrix[rowIndex].hasOwnProperty(k)) {
						firstAvailCol = k;
						break;
					}
				}
				if (! lookup.hasOwnProperty(rowIndex)) {
					lookup[rowIndex] = { };
				}
				lookup[rowIndex][c.cellIndex] = firstAvailCol;
				for (let k = rowIndex; k < rowIndex + rowSpan; k++) {
					if (! matrix.hasOwnProperty(k)) {
						matrix[k] = [];
					}
					for (let l = firstAvailCol; l < firstAvailCol + colSpan; l++) {
						matrix[k][l] = true;
					}
				}
			}
		}
		return lookup;
	}

	private static getNextSort(current: ColumnSortDirection | null): "asc"|"desc" {
		if (current == "asc") {
			return "desc";
		}
		return "asc";
	}

	private getThColumn(th: HTMLTableHeaderCellElement): number {
		return this.thCols[(th.parentNode as HTMLTableRowElement).rowIndex][th.cellIndex];
	}

	private getTrs(): HTMLTableRowElement[] {
		return Array.from(this.table.tBodies[ 0 ].children)
			.filter((tr: Element): tr is HTMLTableRowElement => (tr.tagName == "TR"));
	}

	private init(): void {
		const that = this;
		const ths = this.table.getElementsByTagName("th");

		function onThClick(this: HTMLTableHeaderCellElement, e: MouseEvent|KeyboardEvent) {
			const colSort = this.getAttribute("data-ts-sort") as ColumnSortDirection | null;
			const nextSort = TableSorter.getNextSort(colSort);

			that.sortColumn(that.getThColumn(this), nextSort, !! e.shiftKey);
			e.preventDefault();
		}

		function onKeyDown(this: HTMLTableHeaderCellElement, e: KeyboardEvent): void {
			if (e.keyCode === KeyCodes.SPACE || e.keyCode === KeyCodes.ENTER) {
				return onThClick.call(this, e);
			}
			if (e.keyCode === KeyCodes.UP_ARROW) {
				that.sortColumn(that.getThColumn(this), "asc", !!e.shiftKey);
				e.preventDefault();
			}
			if (e.keyCode === KeyCodes.DOWN_ARROW) {
				that.sortColumn(that.getThColumn(this), "desc", !!e.shiftKey);
				e.preventDefault();
			}
		}

		for (let i = 0; i < ths.length; i++) {
			const sort = ths[i].getAttribute("data-ts-sort");
			if (!sort) {
				continue;
			}
			ths[ i ].setAttribute("tabindex", "0");
			ths[ i ].addEventListener("click", onThClick);
			ths[ i ].addEventListener("keydown", onKeyDown);

			if (sort == "asc" || sort == "desc") {
				that.sortColumn(that.getThColumn(ths[i]), sort, true);
				ths[i].classList.add(that.classes[("SORT_" + sort.toUpperCase()) as keyof SortClasses]);
			}
			ths[i].classList.add(that.classes.SORTABLE);
		}
	}

	private styleColumnSorted(column: number): void {
		const trs = this.getTrs();
		let found = false;
		let k = 0;

		if (! this.cacheCellDisplay) {
			this.columnIsDisplayedCache = { };
		}

		for (let i = 0; i < trs.length; i++) {
			const tds = trs[i].children as HTMLCollectionOf<HTMLTableCellElement>;
			if (! found) {
				k = 0;
				for (let j = 0; j < tds.length; j++) {
					if (k <= column && column < k + tds[j].colSpan) {
						found = true;
						break;
					}
					if (! this.columnIsDisplayedCache.hasOwnProperty(j)) {
						this.columnIsDisplayedCache[ j ] = (getComputedStyle(tds[ j ]).display == "table-cell");
					}
					if (this.columnIsDisplayedCache[ j ]) {
						k += tds[j].colSpan;
					}
				}
			}
			if (found) {
				tds[ k ].classList.add(this.classes.SORTED);
			}
		}
	}

	private getSortDirection(column: number): ColumnSortDirection {
		for (let i = 0; i < this.sortList.length; i++) {
			if (this.sortList[i].column == column) {
				return this.sortList[i].direction;
			}
		}
		return "auto";
	}

	public clearFilters(): void {
		const trs = this.getTrs();
		for (let i = 0; i < trs.length; i++) {
			trs[ i ].classList.remove(this.classes.HIDDEN_BY_FILTER);
		}
		this.update();
	}

	public filterColumn(column: number, values: string|string[], exactMatch: boolean = false) {
		const tbody = this.table.querySelector("tbody");
		if (!tbody) {
			throw new Error("tbody not found");
		}
		const trs = this.getTrs();

		if (! Array.isArray(values)) {
			values = [ values ];
		}

		for (let i = 0; i < trs.length; i++) {
			const td = trs[i].children[column];
			const value = td.getAttribute("data-ts-value");
			let found: boolean = false;

			for (let j = 0; j < values.length; j++) {
				if ((exactMatch && value == values[j]) || (! exactMatch && value && value.indexOf(values[j]) !== -1)) {
					found = true;
					break;
				}
			}
			if (! found) {
				trs[ i ].classList.add(this.classes.HIDDEN_BY_FILTER);
				tbody.appendChild(trs[i]);
			}
		}
	}

	public sortColumn(column: number, direction: ColumnSortDirection, append: boolean = false): void {
		const nextSortList : ColumnSort[] = [];

		if (append) {
			for (let i = 0; i < this.sortList.length; i++) {
				if (this.sortList[i].column != column) {
					nextSortList.push(this.sortList[ i ]);
				}
			}
		}

		this.sortList = nextSortList;
		this.sortList.push({ direction, column });

		this.update();

		this.onColumnSorted.trigger({
			sort:   { column, direction },
			sender: this }
		);
	}

	public update(): boolean {

		const tbody = this.table.tBodies[ 0 ];
		const trs = this.getTrs();
		const ths = this.table.getElementsByTagName("th");
		const tds = tbody.getElementsByTagName("td");

		const comparator = this.comparator;

		for (let i = 0; i < tds.length; i++) {
			tds[ i ].classList.remove(this.classes.SORTED);
		}
		for (let i = 0; i < ths.length; i++) {
			if (!ths[i].getAttribute("data-ts-sort")) {
				continue;
			}
			const col = this.getThColumn(ths[i]);
			const dir = this.getSortDirection(col);

			if (dir != "auto") {
				ths[i].classList.add(this.classes[("SORT_" + dir.toUpperCase()) as keyof SortClasses]);
				ths[i].classList.remove(this.classes[("SORT_" + TableSorter.getNextSort(dir).toUpperCase()) as keyof SortClasses]);

				ths[i].setAttribute("data-ts-sort", dir);
				this.styleColumnSorted(col);
			} else {
				ths[i].setAttribute("data-ts-sort", "auto");
				ths[i].classList.remove(this.classes.SORT_ASC);
				ths[i].classList.remove(this.classes.SORT_DESC);
			}
		}

		function boundComparator(sort: ColumnSort, a: HTMLTableRowElement, b: HTMLTableRowElement) {
			const tdA = a.children[ sort.column ];
			const tdB = b.children[ sort.column ];

			const aNull = !! tdA.getAttribute("data-ts-null");
			const bNull = !! tdB.getAttribute("data-ts-null");

			if (aNull && bNull) {
				return 0;
			}
			if (aNull) {
				return 1;
			}
			if (bNull) {
				return -1;
			}

			const aVal = tdA.getAttribute("data-ts-value");
			const bVal = tdB.getAttribute("data-ts-value");

			let compare = comparator.compare(aVal || "", bVal || "");

			if (compare === 0 && a.hasAttribute("data-row-id") && b.hasAttribute("data-row-id")) {
				compare = comparator.compare(a.getAttribute("data-row-id") || "", b.getAttribute("data-row-id") || "");
			}

			if (sort.direction == "desc") {
				compare *= -1;
			}
			return compare;
		}

		for (let i = this.sortList.length - 1; i >= 0; i --) {
			trs.sort(boundComparator.bind(null, this.sortList[ i ]));
		}

		let changed = false;
		for (let i = 0; i < trs.length; i++) {
			if (tbody.children[i] !== trs[i]) {
				changed = true;
			}
		}

		if (changed) {
			const fragment = document.createDocumentFragment();
			for (let i = 0; i < trs.length; i ++) {
				fragment.appendChild(trs[ i ]);
			}
			tbody.appendChild(fragment);
		}

		return changed;
	}
}
